import { Suspense, lazy, ElementType, ReactElement } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// staff roles
import { rolesForRoute } from '../constants/staffRoles';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) =>
  (
    <Suspense fallback={false}>
      <Component {...props} />
    </Suspense>
  );

// A page opens when one of the user's roles may read its main API route (Appendix A)
const guard = (page: ReactElement, apiPath: string) => (
  <RoleBasedGuard hasContent roles={rolesForRoute('GET', apiPath)}>
    {page}
  </RoleBasedGuard>
);

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        { element: <Navigate to={'login'} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'change-password',
          element: (
            <AuthGuard>
              <ChangePassword />
            </AuthGuard>
          ),
        },
      ],
    },

    // Staff Panel: the same /owner/* routes as the Owner Panel, limited by the staff roles
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/overview" replace />, index: true },

        // Home: who is logged in, the roles and the sections they open
        { path: 'overview', element: <StaffHome /> },

        // Clients
        { path: 'clients', element: <Navigate to="/dashboard/clients/all" replace /> },
        { path: 'clients/all', element: guard(<ClientsList />, '/owner/clients') },
        { path: 'clients/active', element: guard(<ClientsList />, '/owner/clients') },
        { path: 'clients/trial', element: guard(<ClientsList />, '/owner/clients') },
        { path: 'clients/expired', element: guard(<ClientsList />, '/owner/clients') },
        { path: 'clients/blocked', element: guard(<ClientsList />, '/owner/clients') },
        { path: 'clients/deleted', element: guard(<ClientsList />, '/owner/clients') },
        { path: 'clients/:id', element: guard(<ClientDetails />, '/owner/clients/{admin_id}') },

        // Field Agents
        { path: 'agents', element: <Navigate to="/dashboard/agents/all" replace /> },
        { path: 'agents/all', element: guard(<AgentsList />, '/owner/agents') },
        { path: 'agents/active', element: guard(<AgentsList />, '/owner/agents') },
        { path: 'agents/inactive', element: guard(<AgentsList />, '/owner/agents') },
        { path: 'agents/deleted', element: guard(<AgentsList />, '/owner/agents') },

        // Plans & Subscriptions
        { path: 'plans', element: guard(<PlansList />, '/owner/plans') },
        { path: 'subscriptions', element: guard(<SubscriptionsList />, '/owner/subscriptions') },
        { path: 'trial-settings', element: guard(<TrialSettingsPage />, '/owner/trial-settings') },

        // Promo Codes
        { path: 'promo-codes', element: <Navigate to="/dashboard/promo-codes/all" replace /> },
        { path: 'promo-codes/all', element: guard(<PromoCodesList />, '/owner/promocodes') },
        { path: 'promo-codes/expired', element: guard(<PromoCodesList />, '/owner/promocodes') },
        {
          path: 'promo-codes/partner-code-settings',
          element: guard(<PartnerCodeSettingsPage />, '/owner/partner-code-settings'),
        },

        // Payments & Invoices
        { path: 'payments', element: <Navigate to="/dashboard/payments/all" replace /> },
        { path: 'payments/all', element: guard(<PaymentsList />, '/owner/payments') },
        { path: 'payments/successful', element: guard(<PaymentsList />, '/owner/payments') },
        { path: 'payments/pending', element: guard(<PaymentsList />, '/owner/payments') },
        { path: 'payments/failed', element: guard(<PaymentsList />, '/owner/payments') },
        { path: 'invoices', element: guard(<InvoicesList />, '/owner/invoices') },

        // Website Leads
        { path: 'leads', element: <Navigate to="/dashboard/leads/queries" replace /> },
        { path: 'leads/queries', element: guard(<QueriesList />, '/owner/queries') },
        {
          path: 'leads/partner-requests',
          element: guard(<PartnerRequestsList />, '/owner/partner-requests'),
        },

        // Election assets
        { path: 'voter-signs', element: guard(<VoterSignsList />, '/owner/voter-signs') },
        {
          path: 'polling-schedules',
          element: guard(<PollingSchedulesList />, '/owner/polling-schedules'),
        },
        { path: 'locations', element: guard(<LocationMasterPage />, '/owner/locations/districts') },
        {
          path: 'settings/social-platforms',
          element: guard(<SocialPlatformsPage />, '/owner/social-platforms'),
        },

        // Partners
        { path: 'partners', element: <Navigate to="/dashboard/partners/all" replace /> },
        { path: 'partners/all', element: guard(<PartnersList />, '/owner/partners') },
        { path: 'partners/pending', element: guard(<PartnersList />, '/owner/partners') },
        { path: 'partners/approved', element: guard(<PartnersList />, '/owner/partners') },
        { path: 'partners/rejected', element: guard(<PartnersList />, '/owner/partners') },
        { path: 'partners/suspended', element: guard(<PartnersList />, '/owner/partners') },
        { path: 'partners/deleted', element: guard(<PartnersList />, '/owner/partners') },
        {
          path: 'partners/location-requests',
          element: guard(<PartnerLocationRequestsList />, '/owner/partner-location-requests'),
        },
        {
          path: 'partners/:id',
          element: guard(<PartnerDetails />, '/owner/partners/{partner_id}'),
        },
        { path: 'partner-orders', element: guard(<PartnerOrdersList />, '/owner/partner-orders') },

        // Commissions
        { path: 'commissions', element: <Navigate to="/dashboard/commissions/rules" replace /> },
        {
          path: 'commissions/settings',
          element: guard(<CommissionSettingsPage />, '/owner/commission-settings'),
        },
        {
          path: 'commissions/rules',
          element: guard(<CommissionRulesList />, '/owner/commission-rules'),
        },
        {
          path: 'commissions/ledger',
          element: guard(<CommissionLedgerList />, '/owner/commissions'),
        },
        { path: 'commissions/payouts', element: guard(<PayoutsPage />, '/owner/payouts') },
        {
          path: 'commissions/unconfigured',
          element: guard(<UnconfiguredOrdersList />, '/owner/commissions/unconfigured'),
        },
        {
          path: 'commissions/reports',
          element: guard(<PartnerReportsPage />, '/owner/partner-reports/summary'),
        },

        // KYC
        { path: 'kyc', element: <Navigate to="/dashboard/kyc/queue" replace /> },
        { path: 'kyc/settings', element: guard(<KycSettingsPage />, '/owner/kyc-settings') },
        { path: 'kyc/queue', element: guard(<KycQueueList />, '/owner/kyc/queue') },
        {
          path: 'kyc/review/:id',
          element: guard(<PartnerKycReview />, '/owner/partners/{partner_id}/kyc'),
        },

        // Material Wallets
        { path: 'wallets', element: <Navigate to="/dashboard/wallets/overview" replace /> },
        {
          path: 'wallets/overview',
          element: guard(<WalletSummaryPage />, '/owner/wallet/summary'),
        },
        { path: 'wallets/all', element: guard(<WalletsListPage />, '/owner/wallets') },
        {
          path: 'wallets/recharges',
          element: guard(<WalletRechargesListPage />, '/owner/wallet/recharges'),
        },
        {
          path: 'wallets/reconcile',
          element: guard(<WalletReconciliationPage />, '/owner/wallet/reconciliation'),
        },
        {
          path: 'wallets/settings',
          element: guard(<WalletSettingsPage />, '/owner/wallet/settings'),
        },

        // Material Orders & Catalog
        { path: 'material', element: <Navigate to="/dashboard/material/orders" replace /> },
        {
          path: 'material/orders',
          element: guard(<MaterialOrdersListPage />, '/owner/material/orders'),
        },
        // Each report checks its own route; usage is the one most roles share
        {
          path: 'material/reports',
          element: guard(<MaterialReportsPage />, '/owner/material/reports/usage'),
        },
        {
          path: 'material/products',
          element: guard(<MaterialProductsPage />, '/owner/material/products'),
        },
        {
          path: 'material/settings',
          element: guard(<MaterialSettingsPage />, '/owner/material/settings'),
        },
        {
          path: 'material/stuck',
          element: guard(<MaterialStuckJobsPage />, '/owner/material/jobs/stuck'),
        },
        {
          path: 'material/postcards',
          element: guard(<MaterialPostcardsPage />, '/owner/material/postcards'),
        },
        {
          path: 'material/slip-data',
          element: guard(<MaterialSlipDataPage />, '/owner/material/slip-data'),
        },

        // Upload Requests & Updates (mobile app users)
        {
          path: 'upload-requests',
          element: guard(<UploadRequestsList />, '/owner/upload-requests'),
        },
        {
          path: 'upload-requests/:id',
          element: guard(<UploadRequestDetail />, '/owner/upload-requests/{request_id}'),
        },
        { path: 'announcements', element: guard(<AnnouncementsList />, '/owner/announcements') },
        { path: 'announcements/new', element: guard(<NewAnnouncement />, '/owner/announcements') },

        // The bell's "view all": every login (each sees the events of its roles), no route guard
        { path: 'notifications', element: <NotificationsPage /> },
      ],
    },

    // Main / Fallback Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      children: [{ element: <Navigate to={'/dashboard/overview'} replace />, index: true }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const ChangePassword = Loadable(lazy(() => import('../pages/auth/ChangePassword')));

// STAFF PAGES
const StaffHome = Loadable(lazy(() => import('../pages/dashboard/StaffHome')));
const ClientsList = Loadable(lazy(() => import('../pages/clients/ClientsList')));
const ClientDetails = Loadable(lazy(() => import('../pages/clients/ClientDetails')));
const AgentsList = Loadable(lazy(() => import('../pages/agents/AgentsList')));
const PlansList = Loadable(lazy(() => import('../pages/plans/PlansList')));
const SubscriptionsList = Loadable(lazy(() => import('../pages/subscriptions/SubscriptionsList')));
const TrialSettingsPage = Loadable(lazy(() => import('../pages/subscriptions/TrialSettingsPage')));
const PromoCodesList = Loadable(lazy(() => import('../pages/promoCodes/PromoCodesList')));
const MaterialSlipDataPage = Loadable(lazy(() => import('../pages/material/MaterialSlipDataPage')));
const UploadRequestsList = Loadable(
  lazy(() => import('../pages/uploadRequests/UploadRequestsList'))
);
const UploadRequestDetail = Loadable(
  lazy(() => import('../pages/uploadRequests/UploadRequestDetail'))
);
const AnnouncementsList = Loadable(lazy(() => import('../pages/announcements/AnnouncementsList')));
const NewAnnouncement = Loadable(lazy(() => import('../pages/announcements/NewAnnouncement')));
const NotificationsPage = Loadable(lazy(() => import('../pages/notifications/NotificationsPage')));
const PartnerCodeSettingsPage = Loadable(
  lazy(() => import('../pages/promoCodes/PartnerCodeSettingsPage'))
);
const PaymentsList = Loadable(lazy(() => import('../pages/payments/PaymentsList')));
const InvoicesList = Loadable(lazy(() => import('../pages/payments/InvoicesList')));
const QueriesList = Loadable(lazy(() => import('../pages/websiteLeads/QueriesList')));
const PartnerRequestsList = Loadable(
  lazy(() => import('../pages/websiteLeads/PartnerRequestsList'))
);
const VoterSignsList = Loadable(lazy(() => import('../pages/voterSigns/VoterSignsList')));
const PollingSchedulesList = Loadable(
  lazy(() => import('../pages/pollingSchedules/PollingSchedulesList'))
);
const LocationMasterPage = Loadable(lazy(() => import('../pages/locations/LocationMasterPage')));
const PartnersList = Loadable(lazy(() => import('../pages/partners/PartnersList')));
const PartnerDetails = Loadable(lazy(() => import('../pages/partners/PartnerDetails')));
const PartnerOrdersList = Loadable(lazy(() => import('../pages/partners/PartnerOrdersList')));
const PartnerLocationRequestsList = Loadable(
  lazy(() => import('../pages/partners/PartnerLocationRequestsList'))
);
const CommissionSettingsPage = Loadable(
  lazy(() => import('../pages/commissions/CommissionSettingsPage'))
);
const CommissionRulesList = Loadable(
  lazy(() => import('../pages/commissions/CommissionRulesList'))
);
const CommissionLedgerList = Loadable(
  lazy(() => import('../pages/commissions/CommissionLedgerList'))
);
const PayoutsPage = Loadable(lazy(() => import('../pages/commissions/PayoutsPage')));
const KycSettingsPage = Loadable(lazy(() => import('../pages/kyc/KycSettingsPage')));
const KycQueueList = Loadable(lazy(() => import('../pages/kyc/KycQueueList')));
const PartnerKycReview = Loadable(lazy(() => import('../pages/kyc/PartnerKycReview')));
const UnconfiguredOrdersList = Loadable(
  lazy(() => import('../pages/commissions/UnconfiguredOrdersList'))
);
const PartnerReportsPage = Loadable(lazy(() => import('../pages/commissions/PartnerReportsPage')));
const SocialPlatformsPage = Loadable(lazy(() => import('../pages/settings/SocialPlatformsPage')));

// MATERIAL WALLETS (Phase 11a)
const WalletSummaryPage = Loadable(lazy(() => import('../pages/wallets/WalletSummaryPage')));
const WalletsListPage = Loadable(lazy(() => import('../pages/wallets/WalletsListPage')));
const WalletRechargesListPage = Loadable(
  lazy(() => import('../pages/wallets/WalletRechargesListPage'))
);
const WalletReconciliationPage = Loadable(
  lazy(() => import('../pages/wallets/WalletReconciliationPage'))
);
const WalletSettingsPage = Loadable(lazy(() => import('../pages/wallets/WalletSettingsPage')));

// MATERIAL ORDERS (Phase 11b, 11c & 11d)
const MaterialOrdersListPage = Loadable(
  lazy(() => import('../pages/material/MaterialOrdersListPage'))
);
const MaterialReportsPage = Loadable(lazy(() => import('../pages/material/MaterialReportsPage')));
const MaterialProductsPage = Loadable(lazy(() => import('../pages/material/MaterialProductsPage')));
const MaterialSettingsPage = Loadable(lazy(() => import('../pages/material/MaterialSettingsPage')));
const MaterialStuckJobsPage = Loadable(
  lazy(() => import('../pages/material/MaterialStuckJobsPage'))
);
const MaterialPostcardsPage = Loadable(
  lazy(() => import('../pages/material/MaterialPostcardsPage'))
);

// MAIN ERROR PAGES
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
